import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../../components/SEOMeta";
import styles from "../../styles/Button.module.css";
import { useLocation } from "react-router-dom";

// Vercel Security Incident - April 19, 2026
// VERIFIED SOURCES:
// - Vercel Official Security Bulletin: https://vercel.com/kb/bulletin/vercel-april-2026-security-incident
// - CEO Guillermo Rauch's statement
// - Confirmed reporting: Indian Express, The Verge, SecurityWeek (verified claims only)
//
// NOTE: ShinyHunters $2M ransom claim and 580 employee records are reported by news outlets
// but NOT confirmed by Vercel. These are clearly labeled as "Unverified" in the page.

const VercelBreachIncident: React.FC = () => {
  const adContainerRef1 = useRef<HTMLDivElement>(null);
  const adContainerRef2 = useRef<HTMLDivElement>(null);
  const adInitialized1 = useRef(false);
  const adInitialized2 = useRef(false);
  const location = useLocation();

  // ==================== STRUCTURED DATA FOR SEO ENHANCEMENTS ====================
  
  // Article structured data
  const articleStructuredData = {
  "@context": "https://schema.org",
  "@type": "TechArticle",
  "headline": "Vercel Data Breach 2026: Hackers Access Internal Systems via Compromised AI Tool",
  "alternativeHeadline": "Vercel Confirms Security Incident Affecting Limited Customer Subset",
  "description": "On April 19, 2026, Vercel suffered a data breach after attackers compromised Context.ai, a third-party AI tool. Environment variables not marked as 'sensitive' were accessed. A hacker claiming to be ShinyHunters demanded $2 million ransom. Vercel engaged Mandiant and notified law enforcement.",
  "datePublished": "2026-04-19T08:00:00+00:00",
  "dateModified": "2026-04-20T12:00:00+00:00",
  "author": {
    "@type": "Person",
    "name": "Cybersecurity Student",
    "url": "https://cyberannals.com/about"
  },
  "publisher": {
    "@type": "Person",
    "name": "Cybersecurity Student",
    "url": "https://cyberannals.com/about"
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "https://cyberannals.com/blog/vercel-data-breach-2026"
  },
  "keywords": "Vercel breach, data breach, supply chain attack, AI compromise, ShinyHunters, environment variables, Context.ai",
  "articleSection": "Cybersecurity Incidents",
  "inLanguage": "en-US",
  "isAccessibleForFree": true
};

  // Breadcrumb structured data for rich results in search
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://cyberannals.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://cyberannals.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Vercel Data Breach 2026",
        "item": "https://cyberannals.com/blog/vercel-data-breach-2026"
      }
    ]
  };


  // WebPage structured data
  const webpageStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Vercel Data Breach 2026: Hackers Access Internal Systems via AI Tool",
    "description": "Vercel confirms major data breach on April 19, 2026. Environment variables exposed, Context.ai compromise, ShinyHunters claims $2M ransom. Full incident analysis and security recommendations.",
    "url": "https://cyberannals.com/blog/vercel-data-breach-2026",
    "datePublished": "2026-04-19",
    "dateModified": "2026-04-20",
    "inLanguage": "en-US"
  };

  // Combined structured data for single script tag
  const combinedStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      articleStructuredData,
      breadcrumbStructuredData,
      webpageStructuredData
    ]
  };

  // Verified timeline events (from Vercel official bulletin)
  const timelineEvents = [
    {
      time: "Before April 19, 2026",
      event: "Context.ai Platform Compromised",
      description: "Third-party AI tool suffered a broader compromise affecting multiple organizations",
      verified: true
    },
    {
      time: "April 19, 2026",
      event: "Vercel Employee Account Compromised",
      description: "Attacker pivoted from Context.ai to access a Vercel employee's Google Workspace account",
      verified: true
    },
    {
      time: "April 19, 2026",
      event: "Internal Systems Accessed",
      description: "Attacker enumerated Vercel environments and accessed non-sensitive environment variables",
      verified: true
    },
    {
      time: "Post-Discovery",
      event: "Vercel Response Initiated",
      description: "Engaged Mandiant, notified law enforcement, deployed protective measures",
      verified: true
    }
  ];

  // Load Google AdSense ads
  useEffect(() => {
    // FIRST AD: In-article / Fluid format (for content integration)
    if (adContainerRef1.current && typeof window !== 'undefined' && !adInitialized1.current) {
      adInitialized1.current = true;
      adContainerRef1.current.innerHTML = '';
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.style.display = 'block';
      ins.style.textAlign = 'center';
      ins.style.minWidth = '300px';
      ins.style.minHeight = '250px';
      ins.setAttribute('data-ad-client', 'ca-pub-9325373695328890');
      ins.setAttribute('data-ad-slot', '3721511676');  // First ad slot
      ins.setAttribute('data-ad-layout', 'in-article');
      ins.setAttribute('data-ad-format', 'fluid');
      adContainerRef1.current.appendChild(ins);
      setTimeout(() => {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          console.error("Adsense error:", e);
        }
      }, 500);
    }

    // SECOND AD: Display / Auto format (responsive display ad)
    if (adContainerRef2.current && typeof window !== 'undefined' && !adInitialized2.current) {
      adInitialized2.current = true;
      adContainerRef2.current.innerHTML = '';
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.style.display = 'block';
      ins.setAttribute('data-ad-client', 'ca-pub-9325373695328890');
      ins.setAttribute('data-ad-slot', '1789827060');  // Second ad slot (display)
      ins.setAttribute('data-ad-format', 'auto');
      ins.setAttribute('data-full-width-responsive', 'true');
      adContainerRef2.current.appendChild(ins);
      setTimeout(() => {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          console.error("Adsense error:", e);
        }
      }, 500);
    }
  }, [location.pathname]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
      {/* SEOMeta with enhanced keywords */}
      <SEOMeta
        title="Vercel Data Breach 2026"
        description="Vercel confirms major data breach on April 19, 2026. Environment variables exposed, Context.ai compromise, ShinyHunters claims $2M ransom. Full incident analysis and security recommendations for developers."
        keywords="Vercel breach, Vercel data breach 2026, Vercel hacked, Vercel security incident, Vercel April 19 2026, Context.ai breach, ShinyHunters Vercel, Vercel environment variables exposed, Vercel hack news, supply chain attack, AI tool compromise, Vercel security bulletin, Vercel customer data breach, Next.js security, Vercel GitHub tokens, Vercel Google Workspace breach, OAuth app compromise, Vercel Mandiant investigation, Vercel CEO Guillermo Rauch, Vercel ransom demand, Vercel data exfiltration, Vercel breach forum, Vercel affected customers, rotate environment variables, Vercel security recommendations, Vercel incident response, cloud platform breach 2026"
        canonical="https://cyberannals.com/blog/vercel-data-breach-2026"
        ogType="article"
        structuredData={combinedStructuredData}
      />

      {/* Twitter Card meta tags for better social sharing */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@CyberAnnals" />
      <meta name="twitter:title" content="Vercel Data Breach 2026: Hackers Access Internal Systems via AI Tool" />
      <meta name="twitter:description" content="Vercel confirms major data breach on April 19, 2026. Environment variables exposed, Context.ai compromise, $2M ransom demand. Full analysis inside." />
      <meta name="twitter:image" content="https://cyberannals.com/images/vercel-breach-og.jpg" />

      {/* First Advertisement - In-Article / Fluid */}
      <div className="lg:col-span-4 mb-6">
        <div className="text-xs text-slate-400 dark:text-slate-500 mb-2 px-1">Advertisement</div>
        <div className="h-full min-h-[250px] min-w-[250px] w-full bg-white/5 border border-white/10 rounded-2xl relative overflow-hidden"
             id="vercel-breach-ad-1"
             ref={adContainerRef1}>
        </div>
      </div>

      {/* Breaking News Banner */}
      <div className="bg-red-600 dark:bg-red-700 text-white px-4 py-3 rounded-lg mb-6 animate-pulse">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="font-bold text-lg sm:text-xl flex items-center gap-2">
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            CONFIRMED: Vercel Security Incident - April 19, 2026
          </span>
          <span className="text-sm sm:text-base bg-white text-red-600 px-3 py-1 rounded-full font-semibold">
            Official
          </span>
        </div>
      </div>

      <article className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
        {/* Header Section */}
        <header className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 dark:text-white mb-3 sm:mb-4 leading-tight">
            Vercel Data Breach 2026
          </h1>
          <h2 className="text-lg sm:text-xl text-slate-600 dark:text-slate-400 mb-4">
            Popular Cloud Platform Confirms Unauthorized Access via Third-Party AI Tool Compromise
          </h2>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              April 19, 2026
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Hackers Access Internal Systems via Compromised AI Tool
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              5 min read
            </span>
          </div>
        </header>

        {/* Verified Key Alert Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-red-800 dark:text-red-200 text-sm">Incident Type</h3>
                <p className="text-red-600 dark:text-red-300 text-sm font-medium">Unauthorized Internal Access</p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-orange-800 dark:text-orange-200 text-sm">Data Accessed</h3>
                <p className="text-orange-600 dark:text-orange-300 text-sm font-medium">Non-Sensitive Environment Variables</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-green-800 dark:text-green-200 text-sm">Sensitive Variables</h3>
                <p className="text-green-600 dark:text-green-300 text-sm font-medium">Not Accessed (Encrypted)</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-blue-800 dark:text-blue-200 text-sm">Services Status</h3>
                <p className="text-blue-600 dark:text-blue-300 text-sm font-medium">Fully Operational</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-4 sm:space-y-6 text-slate-700 dark:text-slate-300">
          
          {/* Lead Paragraph - summary for featured snippets */}
          <p className="text-base sm:text-lg font-medium summary">
            On <strong>April 19, 2026, Vercel confirmed a security incident</strong> involving unauthorized access to certain internal systems. The breach originated from a compromised third-party AI tool, <strong>Context.ai</strong>, which allowed attackers to pivot into a Vercel employee's Google Workspace account and access internal environments .
          </p>

          {/* Official Confirmation Box */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-green-900 dark:text-green-100 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              ✅ What Vercel Has Officially Confirmed
            </h2>
            <div className="space-y-3 text-green-800 dark:text-green-200">
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Unauthorized access to internal Vercel systems occurred on April 19, 2026 </li>
                <li>Attack originated from compromised third-party AI tool <strong>Context.ai</strong> </li>
                <li>Attacker accessed a Vercel employee's Google Workspace account and pivoted to internal systems </li>
                <li><strong>Environment variables NOT marked as "sensitive"</strong> were accessed </li>
                <li>Environment variables marked as "sensitive" remain encrypted and show <strong>no evidence of access</strong> </li>
                <li>A <strong>limited subset of customers</strong> had credentials compromised; Vercel contacted them directly </li>
                <li><strong>Services remained fully operational</strong> throughout the incident </li>
                <li><strong>Next.js, Turbopack, and open source projects</strong> were analyzed and confirmed safe </li>
                <li>Vercel engaged <strong>Mandiant</strong> for incident response and notified <strong>law enforcement</strong> </li>
                <li>Deployed extensive <strong>protective measures and monitoring</strong> </li>
              </ul>
            </div>
          </div>

          {/* What This Means For You */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              📧 What This Means For You (Based on Official Communications)
            </h2>
            <div className="space-y-3 text-blue-800 dark:text-blue-200">
              <p>According to the official email sent to Vercel customers:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>If you were contacted directly by Vercel:</strong> Your credentials were among the limited subset compromised. Rotate them immediately.</li>
                <li><strong>If you were NOT contacted (most customers):</strong> <span className="font-bold">"We do not have reason to believe that your Vercel credentials or personal data have been compromised"</span> </li>
                <li>The investigation into whether any data was exfiltrated is <strong>ongoing</strong> </li>
                <li>Vercel has committed to contacting customers if further evidence of compromise is found </li>
              </ul>
            </div>
          </div>

          {/* Verified Timeline */}
          <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">Verified Attack Timeline</h2>
            <div className="space-y-3">
              {timelineEvents.map((event, index) => (
                <div key={index} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 pb-3 border-b border-slate-200 dark:border-slate-700 last:border-0">
                  <span className="font-mono text-sm bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {event.time}
                  </span>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white">{event.event}</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Second Advertisement - Display / Auto (Responsive) */}
          <div className="my-6">
            <div className="text-xs text-slate-400 dark:text-slate-500 mb-2 px-1">Advertisement</div>
            <div className="h-full min-h-[250px] min-w-[250px] w-full bg-white/5 border border-white/10 rounded-2xl relative overflow-hidden"
                 id="vercel-breach-ad-2"
                 ref={adContainerRef2}>
            </div>
          </div>

          {/* Official Response - CEO Statement */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-purple-900 dark:text-purple-100 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Official Response: Vercel & CEO Guillermo Rauch
            </h2>
            
            <div className="space-y-4 text-purple-800 dark:text-purple-200">
              <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-purple-100 dark:border-purple-800">
                <p className="italic">
                  "We've identified a security incident that involved unauthorized access to certain internal Vercel systems. We are actively investigating, and we have engaged incident response experts to help investigate and remediate. We have notified law enforcement and will update this page as the investigation progresses."
                </p>
                <p className="text-right text-sm mt-2">— Vercel Official Security Bulletin</p>
              </div>
              
              <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-purple-100 dark:border-purple-800">
                <p className="italic">
                  "All of our focus right now is on investigation, communication to customers, enhancement of security measures, and sanitization of our environments. We've deployed extensive protection measures and monitoring. We've analyzed our supply chain, ensuring Next.js, Turbopack, and our many open source projects remain safe for our community."
                </p>
                <p className="text-right text-sm mt-2">— Guillermo Rauch, CEO of Vercel</p>
              </div>

              <p className="text-sm">
                Rauch described the attacking group as <strong>"highly sophisticated"</strong> and likely <strong>"significantly accelerated by AI"</strong>, noting they moved with surprising velocity and in-depth understanding of Vercel's systems.
              </p>
            </div>
          </div>

          {/* Verified Technical Details */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">Verified Technical Details</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-slate-700 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Attack Chain (Confirmed)
                </h3>
                <p className="text-sm">Context.ai compromise → Google Workspace OAuth token theft → Vercel employee account takeover → Internal system enumeration → Non-sensitive environment variable access</p>
              </div>
              
              <div className="bg-white dark:bg-slate-700 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Malicious OAuth App IOC
                </h3>
                <p className="text-sm font-mono text-xs break-all bg-slate-100 dark:bg-slate-800 p-2 rounded">110671459871-30f1spbu0hptbs60cb4vsmv79i7bbvqj.apps.googleusercontent.com</p>
                <p className="text-sm mt-2">Google Workspace admins should check for and block this OAuth app.</p>
              </div>
            </div>
          </div>

          {/* Unverified Claims Section - Clearly Labeled */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 rounded-r-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-yellow-800 dark:text-yellow-200 mb-3 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              ⚠️ Unverified: Hacker Claims (Not Confirmed by Vercel)
            </h2>
            <div className="space-y-3 text-yellow-800 dark:text-yellow-200">
              <p>A hacker on BreachForums claiming to be part of <strong>"ShinyHunters"</strong> (known for the Rockstar Games breach) has made the following allegations. <strong>None of these have been confirmed by Vercel.</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>$2 million ransom demand</strong> - The hacker offered to sell stolen Vercel data for this amount </li>
                <li><strong>580 employee records</strong> - A sample containing names, email addresses, account status, and timestamps was shared </li>
                <li><strong>Access to GitHub tokens and NPM tokens</strong> - The hacker claimed to have access to these credentials </li>
                <li><strong>Internal Vercel Enterprise dashboard screenshot</strong> - Shared as proof of access </li>
              </ul>
              <div className="bg-white dark:bg-slate-800 rounded-lg p-3 mt-3">
                <p className="text-sm italic">
                  "This is just from Linear as proof, but the access I'm about to give you includes multiple employee accounts with access to several internal deployments, API keys (including some NPM tokens and some GitHub tokens)."
                </p>
                <p className="text-right text-xs mt-1">— Alleged hacker forum post (unverified)</p>
              </div>
              <p className="text-sm mt-2 font-bold">Important Notes:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                <li>Established members of the ShinyHunters group have <strong>denied involvement</strong> in this attack </li>
                <li>Vercel has <strong>not confirmed</strong> any ransom negotiations or payments </li>
                <li>Vercel states the investigation into data exfiltration is <strong>ongoing</strong> </li>
              </ul>
            </div>
          </div>

          {/* Security Recommendations */}
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-600 p-4 sm:p-6 rounded-r-lg mt-6">
            <h3 className="font-bold text-lg text-red-800 dark:text-red-200 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              🛡️ Verified Security Recommendations (From Vercel)
            </h3>
            <div className="space-y-3 text-red-700 dark:text-red-300">
              <p><strong>Vercel recommends ALL customers take these steps for "peace of mind":</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>Review your activity log</strong> in the Vercel dashboard or via CLI for any suspicious activity </li>
                <li><strong>Rotate environment variables</strong> that contain secrets (API keys, tokens, database credentials) if they were NOT marked as "sensitive" </li>
                <li><strong>Use the "sensitive" flag</strong> for all secret values going forward to ensure proper encryption at rest </li>
                <li><strong>Check for the malicious OAuth app</strong> in Google Workspace: <code className="text-xs bg-red-100 dark:bg-red-800 px-1 py-0.5 rounded break-all">110671459871-30f1spbu0hptbs60cb4vsmv79i7bbvqj.apps.googleusercontent.com</code> </li>
                <li><strong>Audit GitHub and Linear integrations</strong> - these were specifically mentioned in hacker claims </li>
              </ul>
            </div>
          </div>

          {/* FAQ Section - For Rich Results */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Was Vercel hacked in 2026?</h3>
                <p className="text-slate-700 dark:text-slate-300">Yes, Vercel confirmed a security incident on April 19, 2026 involving unauthorized access to internal systems via compromised third-party AI tool Context.ai.</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">What data was exposed in the Vercel breach?</h3>
                <p className="text-slate-700 dark:text-slate-300">Environment variables NOT marked as "sensitive" were accessed. Sensitive variables remain encrypted with no evidence of access. A limited subset of customers had credentials compromised.</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Should I rotate my Vercel environment variables?</h3>
                <p className="text-slate-700 dark:text-slate-300">Vercel recommends all customers rotate environment variables that contain secrets if they were not marked as "sensitive", review activity logs, and use the "sensitive" flag going forward.</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Who claimed responsibility for the Vercel breach?</h3>
                <p className="text-slate-700 dark:text-slate-300">A hacker claiming to be part of ShinyHunters on BreachForums claimed responsibility and demanded $2 million. However, Vercel has not confirmed this attribution.</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Are Vercel services still operational?</h3>
                <p className="text-slate-700 dark:text-slate-300">Yes, Vercel services remained fully operational throughout the incident with no disruption to customer deployments.</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Was Next.js affected by the Vercel breach?</h3>
                <p className="text-slate-700 dark:text-slate-300">No, Vercel confirmed that Next.js, Turbopack, and all open source projects were analyzed and remain safe.</p>
              </div>
            </div>
          </div>

          {/* Current Status */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-600 p-4 sm:p-6 rounded-r-lg mt-6">
            <h3 className="font-bold text-lg text-amber-800 dark:text-amber-200 mb-2">
              📌 Current Status - As of April 20, 2026
            </h3>
            <ul className="list-disc list-inside text-amber-700 dark:text-amber-300 space-y-2">
              <li>Investigation ongoing with Mandiant and law enforcement </li>
              <li>Vercel services remain <strong>fully operational</strong> with no disruption </li>
              <li><strong>Sensitive environment variables</strong> confirmed secure (encrypted, no evidence of access) </li>
              <li><strong>Limited subset of customers</strong> directly notified; most customers unaffected </li>
              <li><strong>Next.js, Turbopack, and open source projects</strong> confirmed safe </li>
              <li><strong>Data exfiltration status:</strong> Under investigation - Vercel will contact customers if evidence found </li>
              <li><strong>Context.ai breach</strong> may have affected hundreds of other organizations beyond Vercel </li>
            </ul>
          </div>

          {/* Related Historical Incidents */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">Related Security Incidents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/blog/geo-news-hack-incident-2026" className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span className="font-semibold text-slate-900 dark:text-white">📺 Geo News Hack - March 2026</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">Satellite transmission hijacked with anti-military message</p>
              </Link>
              <Link to="/event/kaseya-vsa-2021" className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span className="font-semibold text-slate-900 dark:text-white">🔐 Kaseya VSA Ransomware Attack</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">Supply chain attack affecting hundreds of businesses</p>
              </Link>
              <Link to="/event/microsoft-solarwinds-sunburst" className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span className="font-semibold text-slate-900 dark:text-white">🎯 SolarWinds Sunburst Attack</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">Major software supply chain compromise</p>
              </Link>
              <Link to="/event/log4j-log4shell-2021" className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span className="font-semibold text-slate-900 dark:text-white">📦 Log4Shell Vulnerability</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">Critical zero-day affecting millions of applications</p>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <footer className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <Link
              to="/blog"
              className="inline-flex items-center px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              ← Back to Blog
            </Link>
            <Link
              to="/decade/2020-2026"
              className="inline-flex items-center px-3 py-2 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
            >
              More 2026 Cyber Attacks →
            </Link>
          </div>

          <div className="mt-6">
            <h3 className="font-bold text-slate-900 dark:text-white mb-3 text-sm sm:text-base">Related Coverage:</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Link to="/decade/2020-2026" className={styles.link}>2026 Cyber Attacks</Link>
              <Link to="/event/supply-chain-attacks" className={styles.link}>Supply Chain Security</Link>
              <Link to="/blog/ai-driven-cyber-attacks" className={styles.link}>AI-Driven Cyber Attacks</Link>
              <Link to="/toolkit/environment-variables" className={styles.link}>Secret Management</Link>
              <Link to="/event/shinyhunters-attacks" className={styles.link}>ShinyHunters History</Link>
              <Link to="/blog/zero-trust-architecture" className={styles.link}>Zero Trust Architecture</Link>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default VercelBreachIncident;
